class HausPriceCalculator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.mode = this.dataset.mode || 'turf';
    this.sqft = this.querySelector('[data-field="sqft"]');
    this.range = this.querySelector('[data-field="sqft-range"]');

    // Isolate calculator controls from Horizon's document-level input handlers.
    this.addEventListener('click', (event) => {
      if (event.target.closest('input, label, button')) event.stopPropagation();
    });
    this.addEventListener('change', (event) => event.stopPropagation());
    this.addEventListener('input', (event) => {
      event.stopPropagation();
      if (event.target === this.sqft) this.range.value = Math.min(Number(this.sqft.value || 0), 1000);
      if (event.target === this.range) this.sqft.value = this.range.value;
      this.update();
    });
    this.querySelector('[data-reset]')?.addEventListener('click', () => {
      this.querySelectorAll('input[type="checkbox"]').forEach((input) => { input.checked = false; });
      const infillBags = this.querySelector('[data-field="infill-bags"]');
      if (infillBags) infillBags.value = 1;
      this.sqft.value = 500;
      this.range.value = 500;
      this.update();
    });
    this.querySelector('[data-use-estimate]')?.addEventListener('click', () => this.copyEstimate());
    this.update();
  }

  money(value) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
  }

  checked(selector) { return Boolean(this.querySelector(selector)?.checked); }

  update() {
    const sqft = Math.max(1, Number(this.sqft.value) || 0);
    if (sqft > 1000) {
      this.renderContactRequired(sqft);
      return;
    }
    this.updateTurf(sqft);
  }

  updateTurf(sqft) {
    const addons = [];
    if (this.checked('[data-addon="hardwater"]')) addons.push(['Hard-water treatment', 50]);
    const infillSelected = this.checked('[data-addon="infill"]');
    const infillQuantity = this.querySelector('[data-infill-quantity]');
    if (infillQuantity) infillQuantity.hidden = !infillSelected;
    if (infillSelected) {
      const infillBags = Math.max(1, Math.min(20, Number(this.querySelector('[data-field="infill-bags"]')?.value) || 1));
      addons.push([`Infill replenishment (${infillBags} ${infillBags === 1 ? 'bag' : 'bags'})`, infillBags * 40]);
    }
    const addonTotal = addons.reduce((sum, item) => sum + item[1], 0);
    const additionalSqft = Math.max(0, sqft - 500);
    const klausSpecialBase = 149 + (additionalSqft * .25);
    const protectThePodBase = 249 + (additionalSqft * .45);
    const deepDiveBase = 349 + (additionalSqft * .65);
    const klausSpecial = klausSpecialBase + addonTotal;
    const protectThePod = protectThePodBase + addonTotal;
    const deepDive = deepDiveBase + addonTotal;

    this.renderTiers([
      { label: 'The Klaus Special', total: klausSpecial, minimum: '$149 starting price · includes up to 500 sq. ft.', monthly: this.frequencyRows(klausSpecialBase, addonTotal) },
      { label: 'Protect the Pod', total: protectThePod, minimum: '$249 starting price · includes up to 500 sq. ft.', monthly: this.frequencyRows(protectThePodBase, addonTotal) },
      { label: 'The Deep Dive', total: deepDive, minimum: '$349 starting price · includes up to 500 sq. ft. · extraction included', monthly: this.frequencyRows(deepDiveBase, addonTotal) }
    ]);
    this.renderRows(addons);
    this.renderAlert('');
    this.querySelector('[data-result-disclaimer]').textContent = `*Monthly amounts reflect a 15% recurring-plan discount on the package service and divide the resulting total across the selected interval. Add-ons remain at their stated price. The first 500 sq. ft. is included; additional turf is priced at $0.25, $0.45, or $0.65 per sq. ft. by package. Final measured area and unusual conditions may change the quote.`;
    this.summary = `HAUS Care | ${sqft.toLocaleString()} sq. ft. | The Klaus Special ${this.money(klausSpecial)} per service | Protect the Pod ${this.money(protectThePod)} per service | The Deep Dive ${this.money(deepDive)} per service${addons.length ? ` | ${addons.map(([l,v]) => `${l}: ${this.money(v)}`).join('; ')}` : ''}`;
  }

  frequencyRows(serviceTotal, addonTotal = 0) {
    const recurringTotal = (serviceTotal * .85) + addonTotal;
    return `<div><span>Quarterly*</span><strong>${this.money(recurringTotal / 3)}/mo.</strong></div><div><span>Seasonally (every 4 months)*</span><strong>${this.money(recurringTotal / 4)}/mo.</strong></div><div><span>Semi-annually*</span><strong>${this.money(recurringTotal / 6)}/mo.</strong></div>`;
  }

  renderTiers(tiers) {
    this.querySelectorAll('[data-tier]').forEach((card, index) => {
      const tier = tiers[index];
      card.querySelector('[data-tier-label]').textContent = tier.label;
      card.querySelector('[data-tier-total]').textContent = `${this.money(tier.total)} per service`;
      card.querySelector('[data-tier-minimum]').textContent = tier.minimum;
      card.querySelector('[data-tier-monthly]').innerHTML = tier.monthly;
    });
  }

  renderRows(rows) {
    const area = this.querySelector('[data-result-rows]');
    area.hidden = rows.length === 0;
    area.innerHTML = rows.length ? `<p>Selected add-ons</p>${rows.map(([label, value]) => `<div><span>${label}</span><strong>${this.money(value)}</strong></div>`).join('')}` : '';
  }

  renderAlert(message) {
    const alert = this.querySelector('[data-result-alert]');
    alert.hidden = !message;
    alert.textContent = message;
  }

  renderContactRequired(sqft) {
    this.querySelectorAll('[data-tier]').forEach((card, index) => {
      card.querySelector('[data-tier-label]').textContent = index === 0 ? 'Large project' : 'Custom scope';
      card.querySelector('[data-tier-total]').textContent = 'Contact HAUS';
      card.querySelector('[data-tier-minimum]').textContent = `${sqft.toLocaleString()} sq. ft. entered`;
      card.querySelector('[data-tier-monthly]').innerHTML = '';
    });
    this.renderRows([]);
    this.renderAlert('Projects over 1,000 square feet require a custom scope and quote. Send us the project details and HAUS will contact you.');
    this.querySelector('[data-result-disclaimer]').textContent = 'Large-project pricing depends on site access, total scope, condition, materials, scheduling, and service location.';
    this.summary = `Custom project review | ${sqft.toLocaleString()} sq. ft. | Contact required; calculator limit is 1,000 sq. ft.`;
  }

  copyEstimate() {
    document.querySelectorAll('[data-haus-calculator-output]').forEach((input) => { input.value = this.summary || ''; });
    const targetId = this.querySelector('[data-use-estimate]')?.getAttribute('href');
    const target = targetId ? document.querySelector(targetId) : null;
    const textarea = target?.querySelector('textarea[name="contact[body]"]');
    if (textarea && !textarea.value.includes('Calculator estimate:')) textarea.value = `${textarea.value ? `${textarea.value}\n\n` : ''}Calculator estimate: ${this.summary || ''}`;
  }
}

if (!customElements.get('haus-price-calculator')) customElements.define('haus-price-calculator', HausPriceCalculator);
